import IconButton from '@/global/components/Button/IconButton'
import { GridCellParams } from '@/global/components/DataGrid/utils/types'
import DevelopmentOnly from '@/global/components/DevelpmentOnly'
import Menu from '@/global/components/MenuV2'
import { useAuth } from '@/hooks'
import { CategoriaEnum, CategoriaID } from '@acme/auth'
import { insensitive, OrigensEnum } from '@acme/common'
import { SN, TipoOcorrencia } from '@acme/core'
import dayjs from 'dayjs'
import { hasPermission } from 'hooks/useBackendMenu'
import {
    ArrowRightLeftIcon,
    CalendarArrowDownIcon,
    CalendarClockIcon,
    CalendarMinus2Icon,
    CalendarMinusIcon,
    CalendarSyncIcon,
    ClockPlusIcon,
    CodeXmlIcon,
    EllipsisVerticalIcon,
    StarIcon,
    Undo2Icon,
} from 'lucide-react'
import { useState } from 'react'
import { Fragment } from 'react/jsx-runtime'
import {
    Empresa,
    Funcionario,
    espelhoPonto,
    funcionario as funcionario_api,
    ocorrencia,
    registros,
    tabelaHorarios,
} from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { useDigitacaoContext } from './pages/DigitacaoLayout'
import { invalidateDigitacaoQueries } from './pages/useDigitacaoData'

interface OpcoesDigitacaoHooks {
    categoria: CategoriaID
    funcionario: Funcionario | null
    empresa: Empresa | null
    hasPermissionOcorrencia: Map<string, boolean>
    canCreateAbonos: boolean
    canCreateCompensacaoFeriados: boolean
    canCreateCompensacaoFolgas: boolean
    canCreateCompensacaoManual: boolean
    canCreateTrocaDiaTrabalho: boolean
    canCreateFaltas: boolean
    canDeleteRegistros: boolean
    canCreateRegistros: boolean
}

export function useOpcoesDigitacaoHooks(): OpcoesDigitacaoHooks {
    const {
        decoded: { categoria },
    } = useAuth()
    const funcionarioId = useStore(state => state.employeeId)
    const { data = null } = funcionario_api.useFetchById(funcionarioId, {
        enabled: funcionarioId > 0,
    })
    const empresa = useStore(state => state.company)

    const canCreateAbonos = hasPermission(['create', 'Abonos'])
    const canCreateCompensacaoFeriados = hasPermission([
        'create',
        'CompensaçãoDeFeriados',
    ])
    const canCreateCompensacaoFolgas = hasPermission([
        'create',
        'CompensaçãoDeFolgas',
    ])
    const canCreateCompensacaoManual = hasPermission([
        'create',
        'CompensaçãoManual',
    ])
    const canCreateTrocaDiaTrabalho = hasPermission([
        'create',
        'TrocaDeDiaDeTrabalho',
    ])
    const canCreateFaltas = hasPermission([
        'create',
        'FaltasJustificadasOuInjustificadas',
    ])
    const canDeleteRegistros = hasPermission(['delete', 'Registros'])
    const canCreateRegistros = hasPermission(['create', 'Registros'])

    // Permissões para todos os tipos de ocorrência
    const canCreateAfastamento = hasPermission(['create', 'Afastamentos'])
    const canCreateCompensacao = hasPermission(['create', 'Compensação'])
    const canCreateAmamentacao = hasPermission(['create', 'Amamentação'])
    const canCreateFolga = hasPermission(['create', 'Folga'])

    const hasPermissionOcorrencia = new Map<string, boolean>([
        [TipoOcorrencia.AFASTAMENTO, canCreateAfastamento],
        [TipoOcorrencia.COMPENSACAO, canCreateCompensacao],
        [TipoOcorrencia.AMAMENTACAO, canCreateAmamentacao],
        [TipoOcorrencia.FOLGA, canCreateFolga],
    ])

    return {
        categoria: categoria as CategoriaID,
        funcionario: data,
        empresa,
        hasPermissionOcorrencia,
        canCreateAbonos,
        canCreateCompensacaoFeriados,
        canCreateCompensacaoFolgas,
        canCreateCompensacaoManual,
        canCreateTrocaDiaTrabalho,
        canCreateFaltas,
        canDeleteRegistros,
        canCreateRegistros,
    }
}

/**
 * Botão de opções leve renderizado por padrão. Só monta o conteúdo completo
 * ({@link OpcoesDigitacaoContent}, com 3 observers de React Query) ao ser
 * clicado, evitando o custo por linha na montagem inicial da grade. Ao ativar,
 * o menu abre automaticamente (defaultOpen).
 */
export function OpcoesDigitacao(props: GridCellParams & OpcoesDigitacaoHooks) {
    const { row, categoria, funcionario } = props
    const [active, setActive] = useState(false)

    // Casos claramente inativos — resolvidos sem observers de RQ.
    if (!row.isAtivo) return <InactiveButton />
    if (
        categoria === CategoriaEnum.Funcionario &&
        !SN.isTrue(funcionario?.ajustesPonto)
    )
        return <InactiveButton />

    if (active) return <OpcoesDigitacaoContent {...props} autoOpen />

    return (
        <IconButton
            tabIndex={-1}
            className="text-foreground"
            onClick={() => setActive(true)}
        >
            <EllipsisVerticalIcon />
        </IconButton>
    )
}

function OpcoesDigitacaoContent({
    row: {
        dia,
        registros: registrosRow,
        faltas,
        horasExtra,
        compensacaoManual,
        justificativa,
        isAtivo,
    },
    categoria,
    funcionario,
    empresa,
    hasPermissionOcorrencia,
    canCreateAbonos,
    canCreateCompensacaoFeriados,
    canCreateCompensacaoFolgas,
    canCreateCompensacaoManual,
    canCreateTrocaDiaTrabalho,
    canCreateFaltas,
    canDeleteRegistros,
    canCreateRegistros,
    autoOpen,
}: GridCellParams & OpcoesDigitacaoHooks & { autoOpen?: boolean }) {
    const { setQueryData } = useDigitacaoContext()
    const { mutateAsync: excluirRegistro } = registros.mutations.useSoftDelete()
    const { mutateAsync: criarRegistro } = registros.mutations.useCreate()
    const { data: ocorrenciasData = [] } = ocorrencia.useFetch()
    const competencia = useStore(state => state.competencia)

    // Só quando o dia tem edição manual (justificativa) e há permissão de edição.
    const podeExcluirEdicao =
        !!justificativa &&
        !!funcionario &&
        hasPermission(['update', 'EdiçãoManualDoEspelhoDoPonto'])

    const excluirEdicaoDia = async () => {
        if (!competencia || !funcionario) return
        try {
            await espelhoPonto.excluirEdicaoDia(funcionario.id, {
                competencia: String(competencia),
                data: dayjs.utc(dia).format('YYYY-MM-DD'),
            })
            toast.success('Edição manual do dia removida')
        } catch (error) {
            toast.error(
                (error as Error)?.message || 'Erro ao excluir a edição do dia',
            )
        } finally {
            await invalidateDigitacaoQueries()
        }
    }

    const ocorrencias = ocorrenciasData
        .filter(({ atalho }) => SN.isTrue(atalho))
        .sort((a, b) => a.descricao.localeCompare(b.descricao))

    // Verifica se o dia tem falta ou extra
    const temFalta = faltas && faltas !== '00:00'
    const temExtra = horasExtra && horasExtra !== '00:00'

    if (!isAtivo) return <InactiveButton title="Inativo" />

    if (categoria === CategoriaEnum.Funcionario) {
        if (SN.isTrue(funcionario?.ajustesPonto))
            return (
                <Menu.Root position="right" defaultOpen={autoOpen} arrow>
                    <Menu.Trigger>
                        <IconButton tabIndex={-1} className="text-foreground">
                            <EllipsisVerticalIcon />
                        </IconButton>
                    </Menu.Trigger>
                    <Menu.List>
                        <Menu.Item href={`abono/adicionar/${dia}`}>
                            Adicionar abono
                        </Menu.Item>
                        <Menu.Item href={`registros/adicionar/${dia}`}>
                            Adicionar registro
                        </Menu.Item>
                    </Menu.List>
                </Menu.Root>
            )
        else return <InactiveButton title="Sem permissão para ajustes" />
    }

    const getCompensacaoManualUrl = () => {
        const params = new URLSearchParams()
        if (temFalta) params.append('tipo', 'falta')
        if (temExtra) params.append('tipo', 'extra')
        const queryString = params.toString()
        return `compensacao-manual/adicionar/${dia}${
            queryString ? `?${queryString}` : ''
        }`
    }

    const menuItems = [
        {
            to: `abono/adicionar/${dia}`,
            label: 'Abono',
            icon: <ClockPlusIcon className="mr-2 opacity-50" />,
            show: canCreateAbonos,
        },
        {
            to: `compensacao-feriado/adicionar/${dia}`,
            label: 'Compensação de feriado',
            icon: <CalendarArrowDownIcon className="mr-2 opacity-50" />,
            show: canCreateCompensacaoFeriados,
        },
        {
            to: `compensacao-folga/adicionar/${dia}`,
            label: 'Compensação de folga',
            icon: <CalendarSyncIcon className="mr-2 opacity-50" />,
            show: canCreateCompensacaoFolgas,
        },
        {
            to: getCompensacaoManualUrl(),
            label: 'Compensação manual',
            icon: <CalendarClockIcon className="mr-2 opacity-50" />,
            show:
                !compensacaoManual &&
                canCreateCompensacaoManual &&
                SN.isTrue(funcionario?.RegrasCalculo?.compensacaoManual),
        },
        {
            to: `troca-dia-trabalho/adicionar/${dia}`,
            label: 'Troca dia trabalho',
            icon: <ArrowRightLeftIcon className="mr-2 opacity-50" />,
            show: canCreateTrocaDiaTrabalho,
        },
        {
            to: `falta-injustificada/adicionar/${dia}`,
            label: 'Falta injustificada',
            icon: <CalendarMinusIcon className="mr-2 opacity-50" />,
            show: canCreateFaltas,
        },
        {
            to: `falta-justificada/adicionar/${dia}`,
            label: 'Falta justificada',
            icon: <CalendarMinus2Icon className="mr-2 opacity-50" />,
            show: canCreateFaltas,
        },
    ]

    // Atalhos de ocorrência (estrela) — sempre ao final do menu.
    const atalhoItems = ocorrencias.map(ocorrencia => ({
        to: `afastamento/adicionar?ocorrenciaId=${ocorrencia.id}&dia=${dia}`,
        label: ocorrencia.descricao,
        icon: (
            <div
                className="flex items-center"
                title={`Atalho rápido\nDefinido na tela de ocorrências`}
            >
                <StarIcon className="mr-2 text-yellow-400 cursor-help opacity-50" />
            </div>
        ),
        show:
            hasPermissionOcorrencia.get(ocorrencia.tipo) &&
            !ocorrencia.disabled,
    }))

    const filteredItems = menuItems.filter(item => item.show)
    const filteredAtalhos = atalhoItems.filter(item => item.show)
    const developmentOptions = [
        canDeleteRegistros && (
            <Menu.Item
                onClick={async () =>
                    Promise.all(
                        registrosRow.map((registro: any) =>
                            excluirRegistro({
                                ...registro,
                                motivo: 'NÃO FAÇA ISSO EM PRODUÇÃO',
                            })
                                .then(() =>
                                    setQueryData('REGISTRO', registro, true),
                                )
                                .catch(() => null),
                        ),
                    )
                }
                className="opacity-70"
            >
                <CodeXmlIcon className="mr-2 text-primary" />
                Limpar registros do dia
            </Menu.Item>
        ),
        canCreateRegistros && !!funcionario && !!empresa && (
            <Menu.Item
                onClick={async () => {
                    const tabelaDia = await tabelaHorarios.funcionario.get({
                        funcionarioId: funcionario.id,
                        dataInicial: dia,
                        dataFinal: dia,
                    })

                    if (tabelaDia.at(0)?.TabelaHorario) {
                        const daySemana = insensitive(
                            dayjs.utc(dia).locale('pt-br').format('ddd'),
                            'lower',
                        )

                        for await (const horario of Object.entries(
                            tabelaDia[0].TabelaHorario,
                        ).filter(
                            ([key]) =>
                                key.startsWith(`${daySemana}Ent`) ||
                                key.startsWith(`${daySemana}Sai`),
                        )) {
                            const [, value] = horario as [string, string]

                            if (value) {
                                await criarRegistro({
                                    hora: value,
                                    data: dia,
                                    empresaId: Number(empresa.id),
                                    funcionarioId: Number(funcionario.id),
                                    statusAprovacao: 'APROVADO',
                                    origem: OrigensEnum.CONTROLE_ACESSO,
                                    __typename: 'Registro',
                                    entradaDiaSeguinte: 'N',
                                    justificativa: '',
                                })
                                    .then(res => {
                                        setQueryData('REGISTRO', res)
                                    })
                                    .catch(() => null)
                            }
                        }
                    }
                }}
                className="opacity-70"
            >
                <CodeXmlIcon className="mr-2 text-primary" />
                Preencher registros do dia
            </Menu.Item>
        ),
    ]
    if (
        filteredItems.length +
            filteredAtalhos.length +
            developmentOptions.length +
            (podeExcluirEdicao ? 1 : 0) >
        0
    ) {
        return (
            <Menu.Root
                position="right"
                arrow
                defaultOpen={autoOpen}
                key={`${ocorrencias.length} + ${empresa?.id}`}
            >
                <Menu.Trigger>
                    <IconButton tabIndex={-1} className="text-foreground">
                        <EllipsisVerticalIcon />
                    </IconButton>
                </Menu.Trigger>
                <Menu.List>
                    {filteredItems.map((item, index) => (
                        <Menu.Item key={index} href={item.to}>
                            {item.icon}
                            {item.label}
                        </Menu.Item>
                    ))}
                    {podeExcluirEdicao && (
                        <Menu.Item
                            onClick={excluirEdicaoDia}
                            className="text-warning realtive hover:bg-warning/10"
                        >
                            <div className="absolute w-1 h-full bg-warning left-0 top-0" />
                            <Undo2Icon className="mr-2" />
                            Excluir edição manual
                        </Menu.Item>
                    )}
                    {filteredAtalhos.map((item, index) => (
                        <Menu.Item key={index} href={item.to}>
                            {item.icon}
                            {item.label}
                        </Menu.Item>
                    ))}
                    <DevelopmentOnly>
                        {developmentOptions.map((option, index) =>
                            option ? (
                                <Fragment key={index}>{option}</Fragment>
                            ) : null,
                        )}
                    </DevelopmentOnly>
                </Menu.List>
            </Menu.Root>
        )
    }

    return (
        <InactiveButton
            title={
                !empresa
                    ? 'Empresa não carregada'
                    : !funcionario
                      ? 'Funcionário não carregado'
                      : !filteredItems.length
                        ? 'Sem permissão'
                        : 'Erro ao carregar opções'
            }
        />
    )
}

function InactiveButton({ title }: { title?: string }) {
    return (
        <div className="cursor-not-allowed">
            <IconButton disabled tabIndex={-1} title={title}>
                <EllipsisVerticalIcon />
            </IconButton>
        </div>
    )
}
